
import React, { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Upload, X } from "lucide-react"

interface ImageUploadProps {
    value?: File | string | null
    onChange: (file: File | null) => void
    label?: string
    className?: string
}

export function ImageUpload({ value, onChange, label = "Загрузить изображение", className }: ImageUploadProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const [preview, setPreview] = useState<string | null>(
        typeof value === "string" ? value : value ? URL.createObjectURL(value) : null
    )

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            const url = URL.createObjectURL(file)
            setPreview(url)
            onChange(file)
        }
    }

    const handleRemove = () => {
        setPreview(null)
        onChange(null)
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }

    // Update preview when value prop changes externally (e.g. initial load)
    React.useEffect(() => {
        if (typeof value === "string") {
            setPreview(value)
        } else if (value instanceof File) {
            setPreview(URL.createObjectURL(value))
        } else if (value === null) {
            setPreview(null)
        }
    }, [value])

    return (
        <div className={className}>
            {preview ? (
                <div className="relative aspect-video w-full max-w-sm rounded-lg overflow-hidden border bg-muted">
                    <img
                        src={preview}
                        alt="Предпросмотр"
                        className="h-full w-full object-cover"
                    />
                    <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute right-2 top-2 h-8 w-8"
                        onClick={handleRemove}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                    >
                        <Upload className="mr-2 h-4 w-4" />
                        {label}
                    </Button>
                    <Input
                        ref={inputRef}
                        type="file"
                        accept="image/*,image/svg+xml,.svg"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </div>
            )}
        </div>
    )
}
